// src/features/rooms/pages/EditRoomPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Alert,
  IconButton,
  CircularProgress,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';

// Components
import RoomForm from '../components/RoomForm';

// Hooks
import { useRooms } from '../hooks/useRooms';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import { useNavigationStack } from '../../../context/NavigationStackContext';
import { FormPage, FormSection, FormActions, FormSwitchField } from '../../../components/common/form';

export default function EditRoomPage() {
  const { t } = useTranslation(['rooms', 'common']);
  const navigate = useNavigate();
  const { roomId } = useParams();
  const { showSuccess, showError } = useSnackbar();
  const { navigateBack } = useNavigationStack();

  // State
  const [room, setRoom] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    area_m2: '',
    classification: '',
    notes: '',
    is_active: true,
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [loading, setLoading] = useState(true);

  // API hooks
  const {
    fetchRoomById,
    updateRoom,
    loading: updating,
    error,
    clearError,
  } = useRooms();

  // Load room data
  useEffect(() => {
    const loadRoomData = async () => {
      if (!roomId) return;

      try {
        setLoading(true);
        const result = await fetchRoomById(roomId);
        
        if (result.success) {
          const roomData = result.data;
          setRoom(roomData);
          setFormData({
            name: roomData.name || '',
            area_m2: roomData.area_m2 || '',
            classification: roomData.classification || '',
            notes: roomData.notes || '',
            is_active: roomData.is_active !== undefined ? roomData.is_active : true,
          });
        } else {
          showError(t('rooms:errors.load_failed'));
        }
      } catch (error) {
        showError(t('rooms:errors.load_failed'));
      } finally {
        setLoading(false);
      }
    };

    loadRoomData();
  }, [roomId, fetchRoomById, showError, t]);

  // Form handling
  const handleInputChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Clear validation error for this field
    setValidationErrors(prev => {
      if (prev[field]) {
        const { [field]: removed, ...rest } = prev;
        return rest;
      }
      return prev;
    });
  }, []);

  const handleActiveToggle = useCallback((event) => {
    handleInputChange('is_active', event.target.checked);
  }, [handleInputChange]);

  const validateForm = useCallback(() => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = t('rooms:validation.name_required');
    } else if (formData.name.length < 2) {
      errors.name = t('rooms:validation.name_min_length');
    } else if (formData.name.length > 255) {
      errors.name = t('rooms:validation.name_max_length');
    }

    if (!formData.area_m2 || formData.area_m2 === '') {
      errors.area_m2 = t('rooms:validation.area_required');
    } else if (parseFloat(formData.area_m2) <= 0) {
      errors.area_m2 = t('rooms:validation.area_positive');
    }

    if (!formData.classification) {
      errors.classification = t('rooms:validation.classification_required');
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData, t]);

  const handleSubmit = useCallback(async (event) => {
    event.preventDefault();

    if (!validateForm() || !room) {
      return;
    }

    try {
      const updateData = {
        name: formData.name.trim(),
        area_m2: parseFloat(formData.area_m2),
        classification: formData.classification,
        notes: formData.notes.trim() || null,
      };

      const result = await updateRoom(room.room_id, updateData);

      if (result.success) {
        showSuccess(t('rooms:success.room_updated'));
        navigate(`/rooms/${room.room_id}`);
      } else {
        showError(result.error || t('rooms:errors.update_failed'));
      }
    } catch (error) {
      showError(t('rooms:errors.unexpected_error'));
    }
  }, [validateForm, room, formData, updateRoom, showSuccess, showError, navigate, t]);

  const handleCancel = useCallback(() => {
    navigateBack(room ? `/rooms/${room.room_id}` : '/rooms');
  }, [navigate, room]);

  // Loading state
  if (loading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  // Not found state
  if (!room) {
    return (
      <Box>
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <IconButton onClick={() => navigate('/rooms')}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" fontWeight="bold">
            {t('rooms:messages.room_not_found')}
          </Typography>
        </Box>
        <Alert severity="error">
          {t('rooms:messages.room_not_found_description')}
        </Alert>
      </Box>
    );
  }

  return (
    <FormPage
      title={t('rooms:edit_room')}
      subtitle={t('rooms:form.edit_title', { name: room.name })}
      crumb={room.name}
      onBack={handleCancel}
      onSubmit={handleSubmit}
      fieldErrors={validationErrors}
      error={error}
      onErrorClose={clearError}
    >
      <RoomForm
        formData={formData}
        onInputChange={handleInputChange}
        validationErrors={validationErrors}
        loading={updating}
        mode="edit"
      />

      <FormSection title={t('rooms:status_form.title')}>
        <FormSwitchField
          checked={formData.is_active}
          onChange={handleActiveToggle}
          disabled={updating}
          label={formData.is_active ? t('rooms:status_form.active_room') : t('rooms:status_form.inactive_room')}
          description={formData.is_active ? t('rooms:status_form.active_description') : t('rooms:status_form.inactive_description')}
        />
      </FormSection>

      <FormActions
        onCancel={handleCancel}
        submitType="submit"
        submitLabel={updating ? t('rooms:actions.updating') : t('rooms:actions.update_room')}
        submitIcon={<SaveIcon />}
        loading={updating}
      />
    </FormPage>
  );
}
